import {
  addDays,
  differenceInCalendarDays,
  format,
  isValid,
  parseISO
} from "date-fns";

import { EVENT_TYPE_WORK_SHIFT_CODE } from "constants/appointments";

export type EventsTag = { type: "Events"; id: string };

/**
 * Provided by every events window that cannot be expressed as days (no dates, or wider than a
 * calendar view), and included in every scoped invalidation. RTK matches an id-scoped
 * invalidation only against providers of that same id — a plain "Events" provider is NOT hit by
 * `{ type: "Events", id: "2026-10-02" }` — so without this id those windows would never refresh
 * on a status update (ENG-2225).
 */
export const UNSCOPED_WINDOW_ID = "unscoped-window";

/**
 * Work shifts never change when an appointment's status does, so they get their own id and a
 * status update leaves them alone. Type-level `invalidateTags(["Events"])` (every mutation that
 * touches events) still refreshes them.
 */
export const WORK_SHIFTS_ID = "work-shifts";

/** Marks a status update whose date could not be read: degrade to refreshing every window. */
export const UNKNOWN_DAY = "*";

// A Month view spans at most 6 weeks (42 days); 62 leaves room for a two-month range before a
// window is treated as unscoped.
const MAX_WINDOW_DAYS = 62;

const tag = (id: string): EventsTag => ({ type: "Events", id });

/**
 * One tag per day in the query's window, so that invalidating a day tag hits exactly the cached
 * windows that contain that day, whatever their shape (Day / Week / Month).
 */
export const eventsWindowTags = (filters?: {
  fromDate?: string;
  toDate?: string;
  eventType?: string;
}): EventsTag[] => {
  if (filters?.eventType === EVENT_TYPE_WORK_SHIFT_CODE) {
    return [tag(WORK_SHIFTS_ID)];
  }
  const from = filters?.fromDate ? parseISO(filters.fromDate) : null;
  const to = filters?.toDate ? parseISO(filters.toDate) : null;
  if (!from || !to || !isValid(from) || !isValid(to)) {
    return [tag(UNSCOPED_WINDOW_ID)];
  }
  const spanDays = differenceInCalendarDays(to, from);
  if (spanDays < 0 || spanDays > MAX_WINDOW_DAYS) {
    return [tag(UNSCOPED_WINDOW_ID)];
  }
  return Array.from({ length: spanDays + 1 }, (_, i) =>
    tag(format(addDays(from, i), "yyyy-MM-dd"))
  );
};

/**
 * What a coalesced burst of status updates invalidates: each distinct day, plus the unscoped
 * windows. `null` means "refresh every events window" (some message had no readable date).
 */
export const statusUpdateInvalidationTags = (
  days: Iterable<string>
): EventsTag[] | null => {
  const distinct = Array.from(new Set(days));
  if (distinct.includes(UNKNOWN_DAY)) {
    return null;
  }
  if (distinct.length === 0) {
    return [];
  }
  return [...distinct.map(tag), tag(UNSCOPED_WINDOW_ID)];
};

/**
 * The clinic-local day of an appointment, from the status-update payload's `start_moment`
 * (`AppointmentNotificationSerializer` localizes it to the clinic's timezone, the same
 * convention as the events query's fromDate/toDate). An appointment cannot cross a clinic-local
 * midnight (`Appointment.clean`), so its start day is its only day.
 */
export const appointmentDayFromMoment = (startMoment?: unknown): string => {
  if (typeof startMoment !== "string") {
    return UNKNOWN_DAY;
  }
  const day = startMoment.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && isValid(parseISO(day))
    ? day
    : UNKNOWN_DAY;
};
